import { useMemo } from 'react';
import { isEmpty } from 'lodash';

import { useTranslation } from '@lyearn/i18n';
import {
  GroupedOptions,
  UseOptionsQueryParams,
  UseOptionsQueryReturnType,
} from '@/components/FilterNew/types';
import usePagination from '@/hooks/usePagination';
import { IntegrationProviderEnum, TagCategoryEnum } from '@/types/schema';

import { useTagFilterQuery } from '../graphql/queries/generated/tagFilter';
import { TagsQueryParams } from '../types';
import { getTagFilterOptionWithConfig } from '..';

export function useTagsOptionsQuery({
  availableOn,
  requiredTag,
  isSharedItem,
  availableInLibrary,
  showProviders = false,
  tagType,
  searchText,
  tagIds,
  notInTagIds,
}: TagsQueryParams & UseOptionsQueryParams): UseOptionsQueryReturnType {
  const { t } = useTranslation(['Tags', 'Jira']);
  const { isLoading, error, data, fetchMore } = usePagination(
    useTagFilterQuery,
    {
      filters: {
        availableOn,
        requiredTag,
        availableInLibrary,
        excludeReceivedTags: isSharedItem,
        name: searchText,
        type: tagType,
        tagIds: isEmpty(tagIds) ? undefined : tagIds,
        notInTagIds: isEmpty(notInTagIds) ? undefined : notInTagIds,
      },
      showProviders,
    },
    {
      edgeKey: 'tags',
      pageSize: -1,
    },
  );

  const groups: GroupedOptions[] = useMemo(() => {
    if (!data.length) {
      return [];
    }

    const lyearnTags = data.filter((tag) => tag.category !== TagCategoryEnum.Field);
    const lyearnTagsOptions = lyearnTags.map((tag) => getTagFilterOptionWithConfig(t, tag));

    const jiraTags = data.filter(
      (tag) =>
        tag.category === TagCategoryEnum.Field &&
        tag.integrationProvider?.provider === IntegrationProviderEnum.Jira,
    );
    const jiraTagsOptions = jiraTags.map((tag) => getTagFilterOptionWithConfig(t, tag));

    const jiraTagsGroup = {
      id: 'jiraTags',
      options: showProviders && jiraTagsOptions.length ? jiraTagsOptions : [],
      label: t('Jira:JiraFields'),
    };

    const lyearnTagsGroup = {
      id: 'lyearnTags',
      options: lyearnTagsOptions.length ? lyearnTagsOptions : [],
      // group label only when jira tags are present
      label: showProviders ? t('Tags:Tags') : undefined,
    };

    return [jiraTagsGroup, lyearnTagsGroup].filter((group) => group?.options.length);
  }, [data, showProviders, t]);

  return { groups, fetching: isLoading, fetchMore, error };
}
